import React, {
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
  useTransition,
} from "react";
import { styles } from "../../utils/styles/careers-styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { colors } from "../../utils/const";
import Link from "next/link";
import Button from "@mui/material/Button";
import { useI18n } from "@/i18n/I18nProvider";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { convertSpacesToHyphens } from "@/utils/general-functions";
import PageHead from "@/components/PageHead";
import { getCareersText } from "../../../locales/careers";
// import CustomHeading from "@/components/custom-heading";

const JobCard = React.memo(function JobCard({ item, locale, t, dir }) {
  const careersText = getCareersText(locale);
  const href = useMemo(() => {
    return `/careers/${item.id}/${convertSpacesToHyphens(item.title)}`;
  }, [item.id, item.title]);

  const jobBriefHTML = useMemo(() => ({ __html: item.jobBrief }), [item.jobBrief]);
  const requirementsHTML = useMemo(
    () => ({ __html: item.requirements }),
    [item.requirements]
  );

  const isAr = locale === "ar";

  return (
    <Box
      sx={{
        width: "100%",
        border: `1px solid ${colors.blue}`,
        borderRadius: "10px",
        color: colors.blue,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box component="h3" sx={{ fontWeight: "bold", fontSize: "1rem", m: 0 }}>
          {item.title}
        </Box>

        <Box sx={{ bgcolor: "#EAEAF3", borderRadius: "5px", p: 0.5 }}>
          {item.section}
        </Box>

        <Button
          aria-label="apply button "
          sx={{
            mr: locale == "ar" ? "auto" : "0",
            ml: locale == "en" ? "auto" : "0",
            color: colors.blue,
          }}
          component={Link}
          href={href}
          // prefetch={false}
        >
          <span>{careersText.careers.apply}</span>
          <KeyboardArrowLeftIcon
            sx={{
              transform: locale === "en" ? "scaleX(-1)" : "scaleX(1)",
            }}
          />
        </Button>
      </Box>

      <Box sx={{ fontWeight: "bold", mb: 1 }}>{careersText.careers.jobBrief}</Box>
      <Box
        sx={{ fontSize: { xs: "12px", sm: "15px" } }}
        dangerouslySetInnerHTML={jobBriefHTML}
      />

      <Box sx={{ fontWeight: "bold", mb: 1 }}>{careersText.careers.requirements}</Box>
      <Box
        sx={{ fontSize: { xs: "12px", sm: "15px" } }}
        dangerouslySetInnerHTML={requirementsHTML}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          gap: 2,
          flexWrap: "wrap",
          fontSize: { xs: "12px", sm: "15px" },
        }}
      >
        <Box>
          <LocationOnIcon
            sx={{
              fontSize: { xs: "18px", sm: "20px" },
              [dir]: "5px",
            }}
          />
          <span>{isAr ? "القاهره" : "Cairo"}</span>
        </Box>

        <Box>
          <AccessTimeIcon
            sx={{
              fontSize: { xs: "18px", sm: "20px" },
              [dir]: "5px",
            }}
          />
          <span>{isAr ? "18 نوفمبر2023" : "18 November 2023"}</span>
        </Box>
      </Box>
    </Box>
  );
});

export default function Index() {
  const { t, locale } = useI18n();
  const careersText = getCareersText(locale);
  let dir = locale === "en" ? "mr" : "ml";

  const [filter, setFilter] = useState("All");

  // INP تحسين: خلي تحديث الفلتر Transition
  const [, startTransition] = useTransition();
  const deferredFilter = useDeferredValue(filter);

  const handleFilter = useCallback(
    (category) => {
      startTransition(() => {
        setFilter(category);
      });
    },
    [startTransition]
  );

  // INP تحسين: فلترة jobs بـ memo + deferredFilter
  const jobs = useMemo(() => {
    return careersText.careers.jobs.filter((item) => {
      if (deferredFilter === "All" || deferredFilter === "الكل") return true;
      return item.section === deferredFilter;
    });
  }, [careersText, deferredFilter]);

  return (
    <>
      <PageHead
        title={t.pages_main_tags.careers.title}
        description={t.pages_main_tags.careers.meta_desc}
      />
      
      <Box
        sx={{
          py: { xs: 2, md: 4, lg: 8 },
          position: "relative",
          display: "inline-block",
          width: "80%",
          height: "auto",
          zIndex: 1,
        }}
        className="horizontal-safe-margin"
      >
        <Box
          sx={{
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.0)",
            zIndex: -1,
          }}
        />

        <Typography
          variant="h2"
          component="h1"
          sx={{
            ...(styles?.title || {}),
          }}
        >
          {careersText.jobs.title}
        </Typography>

        <Box sx={{ maxWidth: "600px", color: colors.blue, mt: 2, mb: 8 }}>
          {careersText.jobs.content}
        </Box>

        <Box
          sx={{
            mx: "30px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {careersText.jobs.jobs_categories.map((category, index) => (
              <Button
                key={index}
                aria-label="Filteration button"
                onClick={() => handleFilter(category)}
                component="h2"
                sx={{
                  bgcolor: category == filter ? "#DFE0ED" : "transparent",
                  color: colors.blue,
                  "&:hover": { bgcolor: "#DFE0ED" },
                  textTransform: "capitalize",
                }}
              >
                {category}
              </Button>
            ))}
          </Box>

          {jobs.length > 0 &&
            jobs.map((item) => (
              <JobCard
                key={item.id}
                item={item}
                locale={locale}
                t={t}
                dir={dir}
              />
            ))}

          {jobs.length == 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: colors.blue,
                bgcolor: "#DFE0ED",
                borderRadius: "5px",
                height: "50px",
              }}
            >
              <Box>{careersText.careers.notHiring}</Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
